import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Code,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { FileText, MagnifyingGlass, WarningCircle } from "@phosphor-icons/react";
import fileinfoService from "../../../api/common/fileinfo.service";
import { getFolderForQuestionnaire, sizeToString } from "../../../api/common/fileinfo";

/**
 * Pick a file already stored in ESG360 (FormKiq-backed) instead of uploading one from disk again.
 *
 * Frontend-only preview: selecting a row does not import it. The data-sorting service can only take a
 * fresh multipart upload (`/upload`) or a JSON API import (`/import-api`) today — there is no endpoint
 * that pulls file bytes from Java/FormKiq by id yet. Ship the picker so the shape of the feature is real
 * and reviewable, and say so on screen (the footer note below) rather than only in this comment, so
 * nobody mistakes "previews" for "works".
 */
export function FileLibraryPicker({ isOpen, onClose, questionnaireId }) {
  const [files, setFiles] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setFiles(null);
    setLoadError(null);
    setSelectedId(null);
    setSearch("");

    (async () => {
      try {
        const page = await fileinfoService.list({ size: 1000 });
        if (cancelled) return;
        setFiles(page?.content || []);
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.detail || "Couldn't load your file library. Try again.",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const taskFolder = getFolderForQuestionnaire(questionnaireId);
  const matchesSearch = (f) => !search.trim() || JSON.stringify(f).toLowerCase().includes(search.trim().toLowerCase());
  const taskFiles = (files || []).filter((f) => f.folders?.includes(taskFolder)).filter(matchesSearch);
  const allFiles = (files || []).filter(matchesSearch);

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="lg">
        <ModalHeader fontSize="18px" fontWeight="700" color="darkTeal">
          Choose from your file library
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loadError ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">{loadError}</AlertDescription>
            </Alert>
          ) : files === null ? (
            <Flex align="center" justify="center" py={10} gap={3} color="gray.500">
              <Spinner size="md" />
              <Text>Loading your files…</Text>
            </Flex>
          ) : (
            <Tabs colorScheme="green" size="sm">
              <TabList>
                <Tab>This task ({taskFiles.length})</Tab>
                <Tab>All files ({allFiles.length})</Tab>
              </TabList>

              <Box mt={3} mb={2}>
                <HStack
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={3}
                  py={1.5}
                  bg="gray.50"
                >
                  <MagnifyingGlass size={14} color="#A0AEC0" />
                  <Input
                    variant="unstyled"
                    fontSize="13px"
                    placeholder="Search by filename…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </HStack>
              </Box>

              <TabPanels>
                <TabPanel px={0}>
                  <FileRows
                    files={taskFiles}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    emptyLabel="No files have been attached to this task yet."
                  />
                </TabPanel>
                <TabPanel px={0}>
                  <FileRows
                    files={allFiles}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    emptyLabel="Your organisation has no files yet."
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}

          <HStack
            mt={4}
            spacing={2}
            align="flex-start"
            bg="orange.50"
            borderRadius="md"
            px={3}
            py={2}
          >
            <Box mt="2px">
              <WarningCircle size={14} color="#DD6B20" weight="fill" />
            </Box>
            <Text fontSize="12px" color="orange.800">
              Preview only — importing from your file library isn&apos;t wired up yet. The data-sorting
              service can only accept a fresh upload or an API import today; there&apos;s no endpoint to
              pull a file&apos;s bytes from FormKiq by id. Backend TODO, mirroring the existing{" "}
              <Code fontSize="11px">/import-api</Code> pattern.
            </Text>
          </HStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose} mr={2}>
            Close
          </Button>
          <Tooltip label="Not available yet — see the note above." openDelay={200}>
            <Button variant="primary" isDisabled>
              Use this file
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function FileRows({ files, selectedId, onSelect, emptyLabel }) {
  if (files.length === 0) {
    return (
      <Text fontSize="13px" color="gray.500" textAlign="center" py={8}>
        {emptyLabel}
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={2} maxH="320px" overflowY="auto">
      {files.map((f) => {
        const isSelected = selectedId === f.id;
        return (
          <HStack
            key={f.id}
            justify="space-between"
            p={3}
            borderWidth="1px"
            borderRadius="md"
            cursor="pointer"
            borderColor={isSelected ? "lime" : "gray.200"}
            bg={isSelected ? "lightLime" : "gray.50"}
            _hover={{ borderColor: "lime" }}
            transition="all 0.15s"
            onClick={() => onSelect(f.id)}
          >
            <HStack spacing={2} minW={0}>
              <FileText size={16} color="#718096" />
              <Text fontSize="13px" fontWeight="600" color="darkTeal" noOfLines={1}>
                {f.filename || f.name || f.id}
              </Text>
            </HStack>
            <Text fontSize="11px" color="gray.500" flexShrink={0}>
              {sizeToString(f.size)}
            </Text>
          </HStack>
        );
      })}
    </VStack>
  );
}

export default FileLibraryPicker;
